// Basic pricing logic composable
import { PERIODS, PERIOD_MULTIPLIERS, PERIOD_SAVED_MONTHS, PERIOD_OFFERS } from "../config/pricingConfig"
import { plansBasic } from "../data/plans"
import { needsCustomPlan } from "../constants/customPlan"

export function usePricingBasic() {
  /**
   * Calculate plan based on basic pricing logic
   * @param {Object} state - Current pricing state
   * @returns {Object} - Calculated plan result
   */
  function calculatePlan(pricingState) {
    const plans = plansBasic

    // Check if custom plan is needed (values exceed maximums)
    if (needsCustomPlan(pricingState)) {
      const customPlan = plans.find((plan) => plan.id === "custom")
      return {
        plan: customPlan,
        finalPrice: 0,
        priceLabel: "",
        savingsBadge: null,
        savingsAmount: 0,
        offerText: null,
        displayValues: {
          socialAccounts: pricingState.socialAccounts,
          users: pricingState.users,
          sources: pricingState.sources,
        },
        extraCosts: {
          social: null,
          users: null,
          sources: null,
        },
        isCustomPlan: true,
        features: customPlan.features,
        period: pricingState.period,
      }
    }

    // Filter out custom plan for normal plan search
    const regularPlans = plans.filter((plan) => plan.id !== "custom")

    // Find suitable plan that covers all requirements
    let suitablePlan = regularPlans.find(
      (plan) =>
        plan.maxSocialAccounts >= pricingState.socialAccounts &&
        plan.maxUsers >= pricingState.users &&
        plan.maxSources >= pricingState.sources
    )

    // Safety fallback if needs exceed the highest plan
    if (!suitablePlan) {
      suitablePlan = regularPlans[regularPlans.length - 1] // Get premium
    }

    // Calculate final price and labels
    let finalPrice = 0
    let priceLabel = "dash.plan.card.price_per_month"
    let savingsBadge = null
    let savingsAmount = 0
    let offerText = null

    switch (pricingState.period) {
      case PERIODS.MONTHLY:
        finalPrice = suitablePlan.priceMonthly
        priceLabel = "dash.plan.card.price_per_month"
        break

      case PERIODS.ONE_YEAR:
        finalPrice = suitablePlan.priceMonthly * PERIOD_MULTIPLIERS[PERIODS.ONE_YEAR]
        priceLabel = ""
        savingsBadge = 'dash.plan.card.savings_badge'
        savingsAmount = suitablePlan.priceMonthly * PERIOD_SAVED_MONTHS[PERIODS.ONE_YEAR]
        offerText = PERIOD_OFFERS[PERIODS.ONE_YEAR]
        break

      case PERIODS.TWO_YEARS:
        finalPrice = suitablePlan.priceMonthly * PERIOD_MULTIPLIERS[PERIODS.TWO_YEARS]
        priceLabel = ""
        savingsBadge = 'dash.plan.card.savings_badge'
        savingsAmount = suitablePlan.priceMonthly * PERIOD_SAVED_MONTHS[PERIODS.TWO_YEARS]
        offerText = PERIOD_OFFERS[PERIODS.TWO_YEARS]
        break
    }

    return {
      plan: suitablePlan,
      finalPrice: Math.round(finalPrice),
      priceLabel,
      savingsBadge,
      savingsAmount,
      offerText,
      displayValues: {
        socialAccounts: suitablePlan.maxSocialAccounts,
        users: suitablePlan.maxUsers,
        sources: suitablePlan.maxSources,
      },
      extraCosts: {
        social: null,
        users: null,
        sources: null,
      },
      isCustomPlan: false,
      features: suitablePlan.features,
      period: pricingState.period,
    }
  }

  return {
    calculatePlan,
  }
}
